/*
 * ⛔ COPIA de `precompro-pay/sdk/precompro-pay.js` (repo precompro-pay). Se copia y no se
 *    carga de pay.precompro.com a propósito: un <script> externo más en el select sería una
 *    puerta más. Si se cambia allá, se copia acá igual.
 */
/*
 * @precompro/pay — SDK del lado del front (select, gift card, prepago, contactless…).
 *
 * JS plano, sin dependencias ni framework: sirve igual en Vue 2, React o una página suelta.
 * Monta el formulario de tarjeta de `pay.precompro.com` en un iframe y devuelve un token.
 * El front NUNCA ve el número ni el CVC.
 *
 *   const pay = PrecomproPay.mount(document.getElementById('tarjeta'), {
 *     vendor: publicId,          // publicId opaco del restaurante (40 o 60 hex)
 *     provider: 'wompi',
 *     theme: 'giftcard',        // opcional: el aspecto del flujo (lista cerrada en pay.js)
 *     style: {                  // opcional: pisa el default. Solo estas claves, validadas:
 *       color, backgroundColor, borderColor, placeholderColor, errorColor, optionBackground,
 *       fontSize, height, borderRadius, borderWidth, paddingX, gap (px), fontWeight, fontFamily
 *     },
 *     styleFrom: el,            // opcional: un input del front (ej. el del titular). Se copia
 *                               // su aspecto real (getComputedStyle), así el iframe se ve igual
 *                               // en cada restaurante sin configurar nada. `style` gana encima.
 *     onState ({ valid, brand }) { this.puedePagar = valid; },
 *     onReady () {}, onError ({ code, message }) {}
 *   });
 *   const { token, brand, last4 } = await pay.tokenize({ holder: 'Nombre del titular' });  // Redeban: + uid, email
 *   pay.destroy();
 *
 * ⛔ La seguridad NO depende de este archivo: si un skimmer lo reemplaza, igual no puede leer
 *    dentro del iframe. Lo que protege es el origen aislado. Esto solo es el cartero.
 */
const PrecomproPay = (function (global) {
  'use strict';

  const SRC = 'precompro-pay';
  const ORIGEN_POR_DEFECTO = 'https://pay.precompro.com';

  // Los rechazos son `Error` con `code`: el front lee `err.code` y `err.message`.
  function falla (code, message) {
    const e = new Error(message);
    e.code = code;
    return e;
  }

  function nonce () {
    const b = new Uint8Array(16);
    global.crypto.getRandomValues(b);
    return Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
  }

  // El aspecto que el navegador le da a un input del front, en las claves que acepta pay.js.
  // Lo que no se pueda leer queda afuera y manda el default del iframe.
  function estiloDesde (el) {
    if (!el || !global.getComputedStyle) return {};
    const cs = global.getComputedStyle(el);
    let ph = {};
    try { ph = global.getComputedStyle(el, '::placeholder'); } catch (e) { ph = {}; }
    const fuente = String(cs.fontFamily || '').split(',')[0].replace(/["']/g, '').trim();
    // Un campo autocompletado lo pinta el navegador (el celeste de Chrome): esos colores no son
    // del restaurante, así que no se copian y manda el default.
    let autocompletado = false;
    try { autocompletado = el.matches(':autofill'); } catch (e) {
      try { autocompletado = el.matches(':-webkit-autofill'); } catch (e2) { autocompletado = false; }
    }
    // Muchos fronts dibujan el borde en la caja que envuelve al input (Buefy: `.field`), no en el
    // input. Si el input no tiene borde, el borde, las esquinas y el alto se sacan de la primera
    // caja con borde hasta 3 niveles arriba.
    let caja = el;
    let cc = cs;
    for (let i = 0, p = el.parentElement; i < 3 && p && !(parseFloat(cc.borderTopWidth) > 0 && cc.borderTopStyle !== 'none'); i++, p = p.parentElement) {
      const cp = global.getComputedStyle(p);
      if (parseFloat(cp.borderTopWidth) > 0 && cp.borderTopStyle !== 'none') { caja = p; cc = cp; }
    }
    return {
      color: autocompletado ? undefined : cs.color,
      backgroundColor: autocompletado ? undefined : cs.backgroundColor,
      borderColor: cc.borderTopColor,
      borderWidth: cc.borderTopWidth,
      borderRadius: cc.borderTopLeftRadius,
      height: caja === el ? cs.height : caja.getBoundingClientRect().height + 'px',
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      fontFamily: fuente,
      paddingX: cs.paddingLeft,
      placeholderColor: ph && ph.color && ph.color !== cs.color ? ph.color : undefined
    };
  }

  // Cuánto se espera el `ready` del iframe antes de darlo por perdido y montar otro. En un
  // celular con datos la conexión a veces no sale o no termina (28-sep-2026: Chrome dejaba el
  // hueco en blanco o la carita de error y la petición NUNCA llegó a pay.precompro.com), y sin
  // esto la persona quedaba sin formulario y sin aviso.
  const ESPERA_READY = 10000;
  // Si el iframe ya disparó `load` y el `ready` no llega en este rato, lo que cargó fue la
  // página de error del navegador: se reintenta sin esperar los 10 s completos.
  const ESPERA_TRAS_LOAD = 2500;
  const INTENTOS = 3;

  // El cargando es el MISMO del selector de horas del select (`icon-precompro-loader-new` +
  // `snipper`): el logo de Precompro girando (1,5 s por vuelta) en el color del tema, sin caja.
  // Va en `currentColor`: toma el color del texto del checkout de cada restaurante, que es el que
  // ya se lee sobre su fondo (negro, rojo, blanco…). El mensaje va debajo, en el mismo color, para
  // que la persona sepa qué pasa. Gira con animación SVG, no con CSS: no depende de hojas de
  // estilo ni de la CSP del front.
  const CAJA = 'display:flex;flex-direction:column;align-items:center;gap:10px;color:inherit;font-size:14px;font-weight:600;line-height:1.35;';
  const LOGO = '<span style="' + CAJA + '">' +
    // viewBox con margen: centrado en el eje de giro (25, 25.1) y con radio 29, porque las puntas
    // de los arcos llegan a ~27,7 del centro y con el viewBox justo (0 0 50 51) se recortaban al girar.
    '<svg width="60" height="60" viewBox="-4 -3.9 58 58" overflow="visible" aria-hidden="true" style="flex:none;opacity:.7">' +
    '<g><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M3.11408 16.9898C4.28241 16.9898 5.36455 16.3386 5.89126 15.2852C6.87764 13.3221 8.18962 11.4834 9.83677 9.84579C11.4744 8.20821 13.313 6.89623 15.2762 5.90027C16.3201 5.37357 16.9808 4.301 16.9808 3.12309C16.9808 0.805581 14.5388 -0.707505 12.4703 0.336332C9.93254 1.61958 7.55757 3.31462 5.44116 5.43102C3.32476 7.54743 1.62015 9.9224 0.346472 12.4602C-0.716518 14.5478 0.796566 16.9898 3.11408 16.9898ZM46.5854 34.1077C47.2558 34.1077 47.8687 34.4524 48.223 35.0175C48.5773 35.592 48.6061 36.2816 48.2996 36.8849C47.0547 39.3556 45.4075 41.6252 43.4252 43.6076C41.4333 45.5995 39.1732 47.2371 36.7025 48.482C36.4248 48.6161 36.1375 48.6927 35.8406 48.6927C35.4863 48.6927 35.1415 48.5969 34.8351 48.4054C34.2605 48.0511 33.9253 47.4382 33.9253 46.7678C33.9253 46.04 34.3275 45.3888 34.9692 45.0632C37.0856 44.0002 39.02 42.5925 40.715 40.8974C42.4101 39.2024 43.8178 37.2679 44.8808 35.1515C45.2064 34.5003 45.8576 34.1077 46.5854 34.1077ZM46.5854 32.6042C45.283 32.6042 44.1243 33.3224 43.5401 34.4716C42.5442 36.4444 41.2418 38.2543 39.6521 39.8344C38.0624 41.4241 36.262 42.7265 34.2892 43.7225C33.14 44.3067 32.4218 45.4654 32.4218 46.7678C32.4218 47.9649 33.0251 49.047 34.0498 49.6791C34.5957 50.0142 35.2181 50.1866 35.8406 50.1866C36.3673 50.1866 36.894 50.0621 37.3729 49.8227C39.9872 48.5011 42.3814 46.7678 44.4786 44.6706C46.5759 42.5733 48.3092 40.1792 49.6308 37.5648C50.167 36.5018 50.1192 35.2569 49.4871 34.2418C48.8646 33.2171 47.7825 32.6042 46.5854 32.6042ZM9.8601 24.7549C9.8601 16.4904 16.5636 9.78685 24.8281 9.78685C33.0927 9.78685 39.7962 16.4904 39.7962 24.7549C39.7962 33.0194 33.0927 39.7229 24.8281 39.7229C16.5636 39.7229 9.8601 33.0194 9.8601 24.7549ZM16.0561 24.7549C16.0561 29.6006 19.9824 33.527 24.8281 33.527C29.6643 33.527 33.5906 29.6006 33.6002 24.7549C33.6002 19.9092 29.6738 15.9828 24.8281 15.9828C19.9824 15.9828 16.0561 19.9092 16.0561 24.7549Z"/>' +
    '<animateTransform attributeName="transform" type="rotate" from="0 25 25.1" to="360 25 25.1" dur="1.5s" repeatCount="indefinite"/>' +
    '</g></svg><span>Cargando el formulario de pago…</span></span>';

  function mount (contenedor, opts) {
    opts = opts || {};
    if (!contenedor) throw new Error('PrecomproPay: falta el contenedor');
    const origen = opts.origin || ORIGEN_POR_DEFECTO;
    let alto = opts.height || 150;
    let n = null;
    let iframe = null;
    let listo = false;
    let intento = 0;
    let reloj = null;
    let pendiente = null;

    // Mientras el iframe carga se ve esto en su lugar; el iframe queda invisible hasta el `ready`.
    const aviso = document.createElement('div');
    aviso.setAttribute('role', 'status');
    aviso.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:10px;min-height:' + alto +
      'px;padding:12px;box-sizing:border-box;text-align:center;font-size:14px;';
    contenedor.appendChild(aviso);

    function mostrarCargando () {
      aviso.innerHTML = LOGO;
      aviso.style.display = 'flex';
      entrar();
    }

    // Aparece con un fundido corto en vez de saltar.
    function entrar () {
      aviso.style.transition = 'none';
      aviso.style.opacity = '0';
      // eslint-disable-next-line no-unused-expressions
      aviso.offsetHeight; // fuerza el reflujo para que la transicion arranque desde opacity 0
      aviso.style.transition = 'opacity .25s ease';
      aviso.style.opacity = '1';
    }

    // Con los colores del tema, igual que el cargando.
    function mostrarFalla () {
      aviso.innerHTML = '';
      const tarjeta = document.createElement('span');
      tarjeta.style.cssText = CAJA + 'max-width:320px;text-align:center;';
      const txt = document.createElement('span');
      txt.textContent = 'No pudimos cargar el formulario de pago. Revisa tu conexión e inténtalo de nuevo.';
      txt.style.fontWeight = '400';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Reintentar';
      btn.style.cssText = 'cursor:pointer;padding:8px 22px;border:1px solid currentColor;border-radius:999px;background:transparent;color:inherit;font:inherit;font-weight:600;';
      btn.addEventListener('click', function () {
        intento = 0;
        cargar();
      });
      // Sin `onError`: el mensaje ya está acá con su botón; el front lo repetía debajo en rojo.
      tarjeta.appendChild(txt);
      tarjeta.appendChild(btn);
      aviso.appendChild(tarjeta);
      entrar();
    }

    function soltarIframe () {
      clearTimeout(reloj);
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
      iframe = null;
    }

    function cargar () {
      soltarIframe();
      listo = false;
      intento++;
      mostrarCargando();
      // Nonce nuevo en cada intento: un mensaje tardío del iframe anterior no se confunde con este.
      n = nonce();

      const url = new URL('/', origen);
      url.searchParams.set('v', opts.vendor || '');
      url.searchParams.set('p', opts.provider || '');
      url.searchParams.set('n', n);
      if (opts.theme) url.searchParams.set('t', opts.theme);
      // Solo el host, sin `https://`: con el esquema en la URL, Cloud Armor corta el iframe con 403.
      url.searchParams.set('o', global.location.host);

      const este = iframe = document.createElement('iframe');
      iframe.src = url.toString();
      iframe.title = 'Datos de la tarjeta';
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('allow', 'payment');
      iframe.setAttribute('referrerpolicy', 'no-referrer');
      // Invisible (no `display:none`: algunos navegadores no cargan un iframe oculto así).
      iframe.style.cssText = 'width:100%;border:0;display:block;height:0;visibility:hidden;background:transparent;';
      iframe.addEventListener('load', function () {
        if (este !== iframe || listo) return;
        clearTimeout(reloj);
        reloj = setTimeout(perdido, ESPERA_TRAS_LOAD);
      });
      contenedor.appendChild(iframe);
      reloj = setTimeout(perdido, ESPERA_READY);
    }

    function perdido () {
      if (listo) return;
      if (intento < INTENTOS) cargar();
      else { soltarIframe(); mostrarFalla(); }
    }

    function alRecibir (ev) {
      if (!iframe || ev.source !== iframe.contentWindow || ev.origin !== origen) return;
      const d = ev.data;
      if (!d || d.src !== SRC || d.n !== n) return;

      if (d.t === 'resize' && d.height > 0) {
        alto = d.height;
        if (listo) iframe.style.height = alto + 'px';
      } else if (d.t === 'ready') {
        if (listo) return;
        listo = true;
        clearTimeout(reloj);
        aviso.style.display = 'none';
        iframe.style.height = alto + 'px';
        iframe.style.visibility = 'visible';
        const estilo = {};
        const desde = estiloDesde(opts.styleFrom);
        Object.keys(desde).forEach(function (k) { if (desde[k] !== undefined && desde[k] !== '') estilo[k] = desde[k]; });
        Object.keys(opts.style || {}).forEach(function (k) { estilo[k] = opts.style[k]; });
        if (Object.keys(estilo).length) enviarEstilo(estilo);
        if (opts.onReady) opts.onReady();
      } else if (d.t === 'state' && opts.onState) opts.onState({ valid: !!d.valid, brand: d.brand || '' });
      else if (d.t === 'token' && pendiente) {
        const res = { token: d.token, brand: d.brand, last4: d.last4, holder: d.holder };
        if (d.status !== undefined) res.status = d.status; // Redeban: valid / review / pending
        if (d.details && typeof d.details === 'object') res.details = d.details;
        pendiente.resolve(res);
        pendiente = null;
      } else if (d.t === 'error') {
        const err = falla(d.code, d.message);
        if (pendiente) { pendiente.reject(err); pendiente = null; } else if (opts.onError) opts.onError(err);
      }
    }
    global.addEventListener('message', alRecibir);
    cargar();

    function enviarEstilo (style) {
      if (!iframe || !listo) return;
      iframe.contentWindow.postMessage({ src: SRC, n, t: 'style', style }, origen);
    }

    return {
      tokenize: function (datos) {
        if (!listo) return Promise.reject(falla('not_ready', 'El formulario de pago todavía no ha cargado.'));
        if (pendiente) return Promise.reject(falla('busy', 'Ya se está validando la tarjeta.'));
        return new Promise(function (resolve, reject) {
          const este = pendiente = { resolve, reject };
          const holder = datos && typeof datos.holder === 'string' ? datos.holder : '';
          const msg = { src: SRC, n, t: 'tokenize', holder };
          // Solo Redeban: el usuario de la tarjeta guardada. Opcionales, el resto los ignora.
          if (datos && typeof datos.uid === 'string') msg.uid = datos.uid;
          if (datos && typeof datos.email === 'string') msg.email = datos.email;
          iframe.contentWindow.postMessage(msg, origen);
          // Si el proveedor no responde, que el botón no quede colgado para siempre. Solo
          // corta ESTE intento: uno viejo no puede rechazar al que vino después.
          setTimeout(function () {
            if (pendiente === este) { pendiente = null; este.reject(falla('timeout', 'La validación de la tarjeta tardó demasiado.')); }
          }, 30000);
        });
      },
      // Cambiar el estilo después de montado (por ejemplo, si el restaurante cambia de tema).
      setStyle: function (style) { enviarEstilo(style); },
      destroy: function () {
        global.removeEventListener('message', alRecibir);
        soltarIframe();
        if (aviso.parentNode) aviso.parentNode.removeChild(aviso);
        listo = false;
        pendiente = null;
      }
    };
  }

  return { mount };
})(window);

export default PrecomproPay;
